import { alertAnatomy, modalAnatomy } from '@chakra-ui/anatomy';
import { createMultiStyleConfigHelpers } from '@chakra-ui/react';

const TEXT_PRIMARY = 'text.primary';
// `bg.elevated`, not `bg.surface`. Since the 2026-09 curved canvas, `bg.surface`
// is the PARCHMENT box drawn ON the white canvas; a modal or drawer floats
// ABOVE everything on its own scrim and stays white (the design sheet draws the
// search modal as "680px white sheet"). `bg.elevated` is that role.
const BG_SURFACE = 'bg.elevated';

// Every modal is capped to the viewport and scrolls its BODY, never spills.
// Before 2026-09-28 the sized variants set `maxH: { base: '100dvh', md: 'auto' }`,
// and `max-height: auto` is not valid CSS: the browser dropped the `md` rule, so
// the mobile `100dvh` cap applied on desktop too, on a `display: block` dialog
// with nothing to scroll. A tall Import modal drew its file list and its Upload
// button OUTSIDE the white box. The cap and the scroll now live here once, for
// every size, so a caller never has to remember `scrollBehavior="inside"`.
// `8rem` keeps a 4rem gutter above and below a dialog that hits the cap: a
// size this file does not override keeps Chakra's `my: 16` at every width, so
// the base cap carries the gutter on a phone too. The sized variants below go
// full screen on mobile (`m: 0`), so they take the whole `100dvh` there.
// `__tests__/modalViewportBound.test.tsx` guards it.
const MODAL_DIALOG_MAX_H = 'calc(100dvh - 8rem)';
const FULL_SCREEN_ON_MOBILE_MAX_H = { base: '100dvh', md: MODAL_DIALOG_MAX_H } as const;

const {
  definePartsStyle: defineModalPartsStyle,
  defineMultiStyleConfig: defineModalMultiStyleConfig,
} = createMultiStyleConfigHelpers(modalAnatomy.keys);

export const Modal = defineModalMultiStyleConfig({
  baseStyle: defineModalPartsStyle({
    overlay: {
      bg: 'blackAlpha.600',
    },
    dialog: {
      bg: BG_SURFACE,
      color: TEXT_PRIMARY,
      // Single source of truth for modal corner radius. xl = 12px.
      // Mobile is full-screen so radius collapses to none. Don't override
      // borderRadius per-call — extend this token if a different shape is
      // ever needed system-wide.
      borderRadius: { base: 'none', md: 'xl' },
      boxShadow: { base: 'none', md: '2xl' },
      // A flex column at every width: header and footer keep their height,
      // the body takes what is left and scrolls (see MODAL_DIALOG_MAX_H).
      display: 'flex',
      flexDirection: 'column',
      maxH: MODAL_DIALOG_MAX_H,
      // Fallback for a dialog that is not header/body/footer (a `<form>`
      // wrapper, a bare Box): it scrolls as a whole rather than spilling.
      // With a ModalBody the body scrolls first, so this never shows.
      overflowY: 'auto',
    },
    header: {
      color: TEXT_PRIMARY,
      fontWeight: 'semibold',
      borderBottom: { base: '1px solid', md: 'none' },
      borderColor: 'border.muted',
      pb: { base: 4, md: 0 },
      flexShrink: 0,
    },
    closeButton: {
      color: 'text.muted',
      _hover: {
        color: TEXT_PRIMARY,
        bg: 'bg.hover',
      },
    },
    body: {
      color: TEXT_PRIMARY,
      px: { base: 4, md: 6 },
      py: { base: 4, md: 6 },
      // Takes the space the header and footer leave, and scrolls inside it.
      // `minH: 0` lets a flex item shrink below its content height; without
      // it the body refuses to shrink and overflows the dialog anyway.
      flex: '1',
      minH: 0,
      overflowY: 'auto',
    },
    footer: {
      borderTop: { base: '1px solid', md: 'none' },
      borderColor: 'border.muted',
      pt: { base: 4, md: 0 },
      px: { base: 4, md: 6 },
      // Mobile: safe area padding for iOS/Android
      pb: { base: 'max(env(safe-area-inset-bottom), 1rem)', md: 6 },
      flexShrink: 0,
    },
  }),
  sizes: {
    full: defineModalPartsStyle({
      dialog: {
        maxW: '100vw',
        // Use dynamic viewport height on mobile to exclude browser chrome
        maxH: { base: '100dvh', md: '100vh' },
        h: { base: '100dvh', md: '100vh' },
        w: '100vw',
        m: 0,
        borderRadius: 'none',
      },
    }),
    lg: defineModalPartsStyle({
      dialog: {
        maxW: { base: '100vw', md: 'lg' },
        // Full screen on mobile; the desktop cap comes from baseStyle.
        maxH: FULL_SCREEN_ON_MOBILE_MAX_H,
        h: { base: '100dvh', md: 'auto' },
        w: { base: '100vw', md: 'auto' },
        m: { base: 0, md: 'auto' },
        borderRadius: { base: 'none', md: 'xl' },
      },
    }),
    md: defineModalPartsStyle({
      dialog: {
        maxW: { base: '100vw', md: 'md' },
        // Full screen on mobile; the desktop cap comes from baseStyle.
        maxH: FULL_SCREEN_ON_MOBILE_MAX_H,
        h: { base: '100dvh', md: 'auto' },
        w: { base: '100vw', md: 'auto' },
        m: { base: 0, md: 'auto' },
        borderRadius: { base: 'none', md: 'xl' },
      },
    }),
    xl: defineModalPartsStyle({
      dialog: {
        borderRadius: { base: 'none', md: 'xl' },
      },
    }),
    '2xl': defineModalPartsStyle({
      dialog: {
        maxW: { base: '100vw', md: '2xl' },
        // Full screen on mobile; the desktop cap comes from baseStyle.
        maxH: FULL_SCREEN_ON_MOBILE_MAX_H,
        h: { base: '100dvh', md: 'auto' },
        w: { base: '100vw', md: 'auto' },
        m: { base: 0, md: 'auto' },
        borderRadius: { base: 'none', md: 'xl' },
      },
    }),
    '3xl': defineModalPartsStyle({
      dialog: {
        maxW: { base: '100vw', md: '3xl' },
        // Full screen on mobile; the desktop cap comes from baseStyle.
        maxH: FULL_SCREEN_ON_MOBILE_MAX_H,
        h: { base: '100dvh', md: 'auto' },
        w: { base: '100vw', md: 'auto' },
        m: { base: 0, md: 'auto' },
        borderRadius: { base: 'none', md: 'xl' },
      },
    }),
  },
  defaultProps: {
    size: 'md',
  },
});

// Alert (AlertDialog) component styles - match Modal styling for consistency
const {
  definePartsStyle: defineAlertPartsStyle,
  defineMultiStyleConfig: defineAlertMultiStyleConfig,
} = createMultiStyleConfigHelpers(alertAnatomy.keys);

export const Alert = defineAlertMultiStyleConfig({
  baseStyle: defineAlertPartsStyle({
    container: {
      bg: BG_SURFACE,
      color: TEXT_PRIMARY,
      borderRadius: { base: 'none', md: 'lg' },
      boxShadow: { base: 'none', md: '2xl' },
    },
    title: {
      color: TEXT_PRIMARY,
    },
    description: {
      color: TEXT_PRIMARY,
    },
    icon: {
      color: TEXT_PRIMARY,
    },
  }),
});
